import React, { useMemo, useState, useEffect, useRef, ChangeEvent } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import Header from "../../components/Header";
import styled from "styled-components";
import { Common } from "../../styles/Common";
import Box from "../../assets/Signup/Box.png";
import SearchIcon from "@src/assets/Signup/Search";

import OfficeInfoCard, { OfficeItem } from "../../components/SignupExpert/OfficeInfoCard";
import OfficeSummaryBox, { OfficeSummary } from "../../components/SignupExpert/OfficeSummaryBox";
import { useAgentOffices, AgentOfficeItem, AgentOfficeParams } from "../../api/expert/expertApi";
import { useExpertInfoStore } from "@src/store/ExpertInfoStore";
import MgInput from "../../components/input/MgInput";

type OfficeSearchProps = {
    sggCode: string;
};

const OfficeSearch: React.FC<OfficeSearchProps> = ({ sggCode }) => {
    const navigate = useNavigate();
    const location = useLocation();

    const [query, setQuery] = useState<string>("");
    const [searched, setSearched] = useState<boolean>(false);
    const [selected, setSelected] = useState<OfficeItem | null>(null);
    const [params, setParams] = useState<AgentOfficeParams>({
        sggCode,
        name: "",
        limit: 0,
        cursor: null,
    });

    const [directInputMode, setDirectInputMode] = useState<boolean>(false);

    const debounceRef = useRef<NodeJS.Timeout | null>(null);

    // 자동 검색
    useEffect(() => {
        if (!query.trim() || directInputMode) return;
        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => {
            setSelected(null);
            setSearched(true);
            setParams({
                sggCode,
                name: query.trim(),
                limit: 20,
                cursor: null,
            });
        }, 500);
        return () => {
            if (debounceRef.current) clearTimeout(debounceRef.current);
        };
    }, [query, directInputMode]);

    // 기존 검색 (버튼 & 엔터 클릭 검색)
    const handleSearch = (e?: React.FormEvent<HTMLFormElement> | React.MouseEvent<HTMLButtonElement>) => {
        if (e) e.preventDefault();
        const name = query.trim();
        if (!name) return;
        setSelected(null);
        setSearched(true);
        setParams({
            sggCode,
            name,
            limit: 20,
            cursor: null,
        });
    };

    const { data, isFetching, isError } = useAgentOffices(params);

    const results: OfficeItem[] = useMemo(() => {
        if (!data?.list) return [];
        return data.list.map(map_to_office_item);
    }, [data]);

    const hasResults = searched && results.length > 0;

    const { form, setPartialState } = useExpertInfoStore();

    // 직접 입력용 필드 변경
    const handleChange =
        <K extends keyof OfficeItem>(key: K) =>
            (e: ChangeEvent<HTMLInputElement>) => {
                setPartialState({
                    infos: [
                        {
                            ...form.infos[0],
                            officeInfo: {
                                ...(form.infos[0].officeInfo ?? {}),
                                [key]: e.currentTarget.value,
                            },
                        },
                    ],
                });
            };

    const handleComplete = () => {
        if (directInputMode) {
            if (!form.infos[0].officeInfo) return;

            const { ceo, name, phone, registrationNumber, address } = form.infos[0].officeInfo;
            if (!ceo || !name || !phone || !registrationNumber || !address) return;

            console.log("[OfficeSearch] 직접입력 완료, 이동 경로: /expert/info");
            navigate("/expert/info", { replace: true, state: { fromOfficeSearch: true } });
            return;
        }

        // 검색 결과가 없고, 직접 입력하기 버튼을 클릭한 경우
        if (!hasResults && searched) {
            setDirectInputMode(true);
            return;
        }

        // 검색 결과가 있고 항목이 선택된 경우
        if (selected) {
            setPartialState({
                infos: [
                    {
                        ...form.infos[0],
                        officeInfo: {
                            name: selected.name,
                            ceo: selected.ceo,
                            address: selected.address,
                            phone: selected.phone,
                            registrationNumber: selected.registrationNumber,
                        },
                    },
                ],
            });
            console.log("[OfficeSearch] 검색 선택 완료, 이동 경로: /expert/info");
            navigate("/expert/info", { replace: true, state: { fromOfficeSearch: true } });
        }
    };

    const buttonLabel = directInputMode
        ? "완료"
        : !searched
            ? "완료"
            : hasResults
                ? "완료"
                : "직접 입력하기";

    const isActive = directInputMode
        ? !!form.infos[0].officeInfo?.ceo &&
        !!form.infos[0].officeInfo?.name &&
        !!form.infos[0].officeInfo?.phone &&
        !!form.infos[0].officeInfo?.registrationNumber &&
        !!form.infos[0].officeInfo?.address
        : !searched
            ? false
            : hasResults
                ? !!selected
                : !!query.trim();

    if (directInputMode) {
        // 직접 입력 화면 렌더링
        const office = form.infos[0].officeInfo ?? {};
        return (
            <Layout>
                <Header title="중개사무소 입력" />
                <Container>
                    <InputBox>
                        <InputHeader>
                            <div className="star">*</div>
                            <div className="title">대표자 성함</div>
                        </InputHeader>
                        <MgInput
                            width="336px"
                            value={office.ceo ?? ""}
                            onChange={handleChange("ceo")}
                            placeholder="대표자 성함 입력"
                            noBorder
                            bgColor="#F4F4F4"
                        />
                    </InputBox>

                    <InputBox>
                        <InputHeader>
                            <div className="star">*</div>
                            <div className="title">중개사무소명</div>
                        </InputHeader>
                        <MgInput
                            width="336px"
                            value={office.name ?? ""}
                            onChange={handleChange("name")}
                            placeholder="중개사무소명 입력"
                            noBorder
                            bgColor="#F4F4F4"
                        />
                    </InputBox>

                    <InputBox>
                        <InputHeader>
                            <div className="star">*</div>
                            <div className="title">연락처</div>
                        </InputHeader>
                        <MgInput
                            width="336px"
                            value={office.phone ?? ""}
                            onChange={handleChange("phone")}
                            placeholder="연락처 입력"
                            noBorder
                            bgColor="#F4F4F4"
                        />
                    </InputBox>

                    <InputBox>
                        <InputHeader>
                            <div className="star">*</div>
                            <div className="title">등록번호</div>
                        </InputHeader>
                        <MgInput
                            width="336px"
                            value={office.registrationNumber ?? ""}
                            onChange={handleChange("registrationNumber")}
                            placeholder="등록번호 입력"
                            noBorder
                            bgColor="#F4F4F4"
                        />
                    </InputBox>

                    <InputBox>
                        <InputHeader>
                            <div className="star">*</div>
                            <div className="title">소재지</div>
                        </InputHeader>
                        <MgInput
                            width="336px"
                            value={office.address ?? ""}
                            onChange={handleChange("address")}
                            placeholder="주소 입력"
                            noBorder
                            bgColor="#F4F4F4"
                        />
                    </InputBox>
                </Container>

                <Footer>
                    <PrimaryBtn type="button" className={isActive ? "active" : ""} onClick={handleComplete} disabled={!isActive}>
                        완료
                    </PrimaryBtn>
                </Footer>
            </Layout>
        );
    }

    return (
        <Layout>
            <Header title="중개사무소 조회" />
            <Container>
                <form onSubmit={handleSearch}>
                    <SearchBar>
                        <IconButton type="button" aria-label="검색" onClick={handleSearch}>
                            <SearchIcon />
                        </IconButton>
                        <SearchInput
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            placeholder="중개사무소명을 입력하세요"
                            autoComplete="off"
                        />
                    </SearchBar>
                </form>

                {hasResults && !selected && !isFetching && (
                    <List>
                        {results.map((r) => (
                            <li key={r.id}>
                                <OfficeSummaryBox
                                    item={
                                        {
                                            id: r.id,
                                            name: r.name,
                                            ceo: r.ceo,
                                            address: r.address,
                                        } as OfficeSummary
                                    }
                                    onClick={() => setSelected(r)}
                                />
                            </li>
                        ))}
                    </List>
                )}

                {selected && <OfficeInfoCard item={selected} />}

                {searched && !hasResults && !isFetching && !isError && (
                    <EmptyState>
                        <img src={Box} alt="검색 결과 없음" />
                        <p>
                            일치하는 정보가 없어요!
                            <br />
                            직접 입력해 주세요.
                        </p>
                    </EmptyState>
                )}
            </Container>

            <Footer>
                <PrimaryBtn type="button" className={isActive ? "active" : ""} onClick={handleComplete} disabled={!isActive}>
                    {buttonLabel}
                </PrimaryBtn>
            </Footer>
        </Layout>
    );
};

export default OfficeSearch;

function map_to_office_item(x: AgentOfficeItem): OfficeItem {
    return {
        id: String(x.no),
        name: x.name,
        ceo: x.ceo,
        address: x.address,
        phone: x.phone,
        registrationNumber: x.registrationNumber,
    };
}

const Layout = styled.div``;

const Container = styled.div`
    padding: 24px;
    position: relative;
    height: 100vh;
    padding-bottom: 120px;
    overflow-y: scroll;
`;

const SearchBar = styled.div`
    width: 100%;
    padding: 12px 14px;
    border: 1px solid #2275ee;
    background-color: #fbf8f8;
    border-radius: 14px;
    display: flex;
    align-items: center;
    gap: 8px;
`;

const IconButton = styled.button`
    display: inline-flex;
    width: 20px;
    height: 20px;
    flex: 0 0 20px;
    border: 0;
    background: transparent;
    padding: 0;
    cursor: pointer;

    svg {
        width: 100%;
        height: 100%;
        display: block;
    }
`;

const SearchInput = styled.input`
    flex: 1;
    border: none;
    background: transparent;
    outline: none;
    font-size: 16px;
    line-height: 1.4;
    color: ${Common.colors?.normalTextColor ?? "#111"};
    ::placeholder {
        color: #9da6b3;
    }
`;

const List = styled.ul`
    margin-top: 16px;
    li + li {
        margin-top: -1px;
    }
`;

const Footer = styled.div`
    position: fixed;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 10;
    padding: 24px;
`;

const PrimaryBtn = styled.button`
    width: 100%;
    padding: 14px 16px;
    border-radius: 12px;
    background: #c3c3c3;
    border: 1px solid #c3c3c3;
    color: #ffffff;
    font-weight: 400;
    font-size: 18px;
    cursor: pointer;

    &:disabled {
        cursor: not-allowed;
        opacity: 0.8;
    }

    &.active {
        background: ${Common.colors.aucMarkerColor};
        border: 1px solid ${Common.colors.aucMarkerColor};
        color: #ffffff;
    }
`;

const EmptyState = styled.div`
    display: flex;
    flex-direction: column;
    text-align: center;
    justify-content: center;
    align-items: center;
    color: #333;
    font-size: 18px;
    font-weight: 400;
    line-height: 140%;
    min-height: calc(100vh - 60px - 120px);

    img {
        width: 124px;
        height: 83px;
        margin-bottom: 12px;
    }
`;

const InputBox = styled.div`
    margin-bottom: 24px;
`;

const InputHeader = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 4px;
    margin-bottom: 8px;
    .star {
        color: ${Common.colors.notiColor};
    }
    .title {
        color: ${Common.colors.normalTextColor};
        font-size: 16px;
        font-weight: 400;
        line-height: 140%;
    }
`;

